/**
 * Section: Content expansion
 * ------------------------------------------------------------------------------
 * Used on the Santander template page.
 *
 * @namespace contentExpansion
 */

import log from 'salvo-lite/log'

import { on } from '../helpers/utils'
import cssClasses from '../helpers/cssClasses'

class ContentExpansion {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem

        this._selectors = {
            button: '[js-expansion="button"]',
            buttonText: '[js-expansion="buttonText"]',
            content: '[js-expansion="content"]'
        }

        this._nodeSelectors = {
            button: [...document.querySelectorAll(this._selectors.button)],
            buttonText: [...document.querySelectorAll(this._selectors.buttonText)],
            content: [...document.querySelectorAll(this._selectors.content)]
        }
        log.debug(this.constructor.name, 'Constructed', this)
    }

    onInit () {
        if (window.contentExpansionLoaded) return

        this.setEventListeners()
        this.checkExpansionStatus()

        window.contentExpansionLoaded = true
    }

    /**
     * Set event listeners.
     */
    setEventListeners () {
        this._nodeSelectors.button.forEach((button) => {
            on('click', button, () => this.handleButtonClick(button))
        })
    }

    /**
     * Check to see if content should be collapsed on page load.
     */
    checkExpansionStatus () {
        this._nodeSelectors.button.forEach((button) => {
            const expanded = button.getAttribute('data-expanded')

            if (expanded === 'false') {
                const expansionId = button.getAttribute('data-expansion-id')

                this.setButtonCloseState(button)
                this.closeExpansionContent(expansionId)
            }
        })
    }

    /**
     * Handle an expansion button being is clicked.
     * @param {HTMLElement} button the clicked button.
     */
    handleButtonClick (button) {
        const expansionId = button.getAttribute('data-expansion-id')
        const expanded = button.getAttribute('data-expanded')

        if (expanded === 'true') {
            this.setButtonCloseState(button)
            this.closeExpansionContent(expansionId)
            return
        }

        this.setButtonOpenState(button)
        this.openExpansionContent(expansionId)
    }

    /**
     * Set button to opened/read less state.
     * @param {HTMLElement} button the active button.
     */
    setButtonOpenState (button) {
        button.classList.remove(cssClasses.collapsed)
        button.setAttribute('data-expanded', true)
        button.querySelector(this._selectors.buttonText).innerText = window.theme.strings.santander.readLess
        this._dataLayer(button, 'Accordion Opened')
    }

    /**
     * Set button to closed/read more state.
     * @param {HTMLElement} button the active button.
     */
    setButtonCloseState (button) {
        button.classList.add(cssClasses.collapsed)
        button.setAttribute('data-expanded', false)
        button.querySelector(this._selectors.buttonText).innerText = window.theme.strings.santander.readMore
        this._dataLayer(button, 'Accordion Closed')
    }

    /**
     * Expand the expansion content.
     * @param {String} id the expansion ID.
     */
    openExpansionContent (id) {
        this._nodeSelectors.content.forEach((content) => {
            if (content.getAttribute('data-expansion-id') === id) {
                content.classList.remove(cssClasses.collapsed)
            }
        })
    }

    /**
     * Collapse the expansion content.
     * @param {String} id the expansion ID.
     */
    closeExpansionContent (id) {
        this._nodeSelectors.content.forEach((content) => {
            if (content.getAttribute('data-expansion-id') === id) {
                content.classList.add(cssClasses.collapsed)
            }
        })
    }

    /**
     * Send title and subtitle content to the DataLayer.
    */
    _dataLayer (button, accordionAction) {
        const accordionTitle = button.parentElement.querySelector('h2.ew-content__header').innerText
        const accordionSubTitle = button.parentElement.querySelector('p.ew-content__text').innerText

        window.dataLayer.push({
            accordionTitle,
            accordionSubTitle,
            accordionAction
        })
    }
}

export default ContentExpansion



// WEBPACK FOOTER //
// ./src/resources/scripts/components/ContentExpansion.js